import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import i18next from 'i18next'

import type { PlantMasterRow } from '~/hooks/useListPlantMasters'
import { convertAreaLabel } from '~/models/area'
import { decimalToNumber } from '~/models/decimal'
import type { CsvColumnDef } from '~/pages/AdminOcctoMaster/tabs/_common/models/csvExport'
import { voltageClassLabel } from '~/pages/AdminOcctoMaster/tabs/_common/models/voltageClass'

/**
 * PowerType の表示ラベルを返す。
 * UNSPECIFIED は空文字を返す (= 選択未了の状態)。
 */
export function plantPowerTypeLabel(powerType: PowerType | undefined): string {
  if (powerType === undefined || powerType === PowerType.UNSPECIFIED) {
    return ''
  }
  const t = i18next.t.bind(i18next)
  switch (powerType) {
    case PowerType.NON_ADJUSTABLE:
      return t('page:adminOcctoMaster.plantMaster.powerType.nonAdjustable')
    case PowerType.ADJUSTABLE:
      return t('page:adminOcctoMaster.plantMaster.powerType.adjustable')
    case PowerType.FIT_EXCEPTION_1:
      return t('page:adminOcctoMaster.plantMaster.powerType.fitException1')
    case PowerType.FIT_EXCEPTION_2:
      return t('page:adminOcctoMaster.plantMaster.powerType.fitException2')
    case PowerType.FIT_EXCEPTION_3:
      return t('page:adminOcctoMaster.plantMaster.powerType.fitException3')
    case PowerType.FIP:
      return t('page:adminOcctoMaster.plantMaster.powerType.fip')
    default:
      return ''
  }
}

/**
 * 発電所マスタタブの CSV エクスポート カラム定義
 * (spec AC-012b / design doc §5「タブ別 CSV カラム」)。
 * List レスポンスのフィールドのみを対象とし (AC-04)、詳細モーダルでのみ
 * 見える合同所有者リストは含まない。
 */
export function plantMasterCsvColumns(): Array<CsvColumnDef<PlantMasterRow>> {
  return [
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.plantCode',
      accessor: (row) => row.plantCode,
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.plantName',
      accessor: (row) => row.plantName,
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.plantNameAbbr',
      accessor: (row) => row.plantNameAbbr,
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.areaCode',
      accessor: (row) => convertAreaLabel(row.areaCode),
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.powerType',
      accessor: (row) => plantPowerTypeLabel(row.powerType),
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.voltageClass',
      accessor: (row) => voltageClassLabel(row.voltageClass),
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.maxReceivingPowerKw',
      accessor: (row) =>
        decimalToNumber(row.maxReceivingPowerKw)?.toString() ?? '',
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.businessName',
      accessor: (row) => row.businessName,
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.contractIdNo2',
      accessor: (row) => row.contractIdNo2 ?? '',
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.priority',
      accessor: (row) => row.priority?.toString() ?? '',
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.proRataPriority',
      accessor: (row) => row.proRataPriority?.toString() ?? '',
    },
    {
      headerKey: 'page:adminOcctoMaster.plantMaster.column.note',
      accessor: (row) => row.note ?? '',
    },
  ]
}
